// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WebsiteElementData } from '@way/theme-host/shared/components/website-element';
import {
  ModernPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { buildHeaderCtaButton } from '../shared/header-cta.util';
import { ModernPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import { HeroLayout1PreviewComponent } from '../../panels/hero-panel/layouts/layout_1';
import { HeroLayout2PreviewComponent } from '../../panels/hero-panel/layouts/layout_2';
import { HeroLayout3PreviewComponent } from '../../panels/hero-panel/layouts/layout_3';
import { HeroLayout4PreviewComponent } from '../../panels/hero-panel/layouts/layout_4';
import { HeroLayout5PreviewComponent } from '../../panels/hero-panel/layouts/layout_5';
import { HeroLayout6PreviewComponent } from '../../panels/hero-panel/layouts/layout_6';
import {
  BookingLayout1PreviewComponent,
  BookingLayout1Data,
} from '../../panels/booking-panel/layouts/layout_1';
import {
  BookingLayout2PreviewComponent,
  BookingLayout2Data,
} from '../../panels/booking-panel/layouts/layout_2';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement extends Omit<WebsiteElementData, 'selected'> {
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  selected?: boolean; // Make selected optional for compatibility
  content: {
    title?: string;
    titleStyle?:
      | string
      | {
          fontWeight?: 'bold' | null;
          fontStyle?: 'italic' | null;
          textDecoration?: 'underline' | null;
          textAlign?: 'left' | 'center' | 'right' | 'justify' | null;
        }; // Text color/style from title.style.text_style (string) or object (minimal theme compatibility)
    backgroundColor?: string; // Background color from layout.background_color
    subtitle?: string;
    description?: string;
    buttonText?: string;
    buttonLink?: string; // Button navigation link
    button?: {
      text?: string;
      backgroundColor?: string;
      textColor?: string;
    };
    imageUrl?: string; // Legacy support
    image?: {
      src?: string;
      alt?: string;
      width?: string;
      height?: string;
      scaletype?: 'fill' | 'fit' | 'stretch';
    };
    backgroundImage?: string; // For full background images (e.g., hero section)
    layout?:
      | 'layout_1'
      | 'layout_2'
      | 'layout_3'
      | 'layout_4'
      | 'layout_5'
      | 'layout_6'
      | 'layout_7'
      | '1'
      | '2'
      | '3'
      | '4'
      | '5'
      | '6'
      | '7'
      | {
          type:
            | 'layout_1'
            | 'layout_2'
            | 'layout_3'
            | 'layout_4'
            | 'layout_5'
            | 'layout_6'
            | 'layout_7';
          grid_columns?: number;
          image_alignment?: string;
          padding?: string;
          background_color?: string;
        }; // Hero layout type - supports both old format (string) and new API format (object with type)
    testimonials?: {
      id: string;
      text: string;
      author: {
        name?: string;
        title?: string;
        avatar?: string;
      };
      rating?: number;
      date?: string;
    }[];
    services?: {
      id?: string;
      name?: string;
      title?: string;
      description?: string;
      price?: string;
      duration?: string;
      image?: string;
      icon?: string;
    }[];
    formFields?: {
      id: string;
      name: string;
      label: string;
      type: string;
      placeholder?: string;
      required: boolean;
      validation?: any;
      options?: {
        value: string;
        label: string;
        disabled?: boolean;
      }[];
    }[];
    availableServices?: {
      id: string;
      name: string;
      description: string;
      price: string;
      duration: string;
      available: boolean;
    }[];
    map?: {
      image: string;
      alt: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
      pin?: {
        image: string;
        alt: string;
      };
    };
    address?: string;
    phone?: string;
    email?: string;
    mapEmbedUrl?: string;
    contactInfo?: {
      address?: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
      phone?: string;
      email?: string;
      website?: string;
    };
    socialLinks?: {
      platform: string;
      url?: string;
      link?: string;
      icon?: string;
      label?: string;
    }[];
    businessHours?: {
      day: string;
      openTime: string;
      closeTime: string;
      closed: boolean;
    }[];
    logo?: {
      image: string;
      alt: string;
      link: string;
    };
    navigation?: {
      id: string;
      label: string;
      link: string;
      active: boolean;
    }[];
    ctaButton?: {
      text: string;
      link: string;
      style: string;
    };
    mobileMenu?: boolean;
  };
}

@Component({
  selector: 'app-modern-home-page-preview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    PageFooterComponent,
    HeroLayout1PreviewComponent,
    HeroLayout2PreviewComponent,
    HeroLayout3PreviewComponent,
    HeroLayout4PreviewComponent,
    HeroLayout5PreviewComponent,
    HeroLayout6PreviewComponent,
    BookingLayout1PreviewComponent,
    BookingLayout2PreviewComponent,
  ],
  templateUrl: './home-page-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home-page-preview.component.scss'],
})
export class ModernHomePagePreviewComponent implements OnChanges, AfterViewInit {
  @ViewChild('testimonialsScroll') testimonialsScrollRef?: ElementRef<HTMLElement>;

  testimonialsScrollAtStart = true;
  testimonialsScrollAtEnd = false;
  @Input() elements: WebsiteElement[] = [];
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Input() pageData: any = null; // Page data to access json_data.website.header and json_data.website.footer
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  // Cache for map embed URL to prevent flickering
  private cachedMapEmbedUrl: SafeResourceUrl | null = null;
  private cachedMapEmbedUrlSource: string | null = null;

  constructor(
    private logger: LoggerService,
    private sanitizer: DomSanitizer
  ) {}

  // Element configuration for reusability
  readonly ELEMENT_CONFIG = {
    header: { class: 'header-element', index: 0 },
    hero: { class: 'hero-element', index: 1 },
    welcome: { class: 'section-element', index: 2 },
    testimonials: { class: 'testimonials-element', index: 3 },
    services: { class: 'services-element', index: 4 },
    booking: { class: 'booking-element', index: 5 },
    contact: { class: 'contact-element', index: 6 },
    footer: { class: 'footer-element', index: 7 },
  };

  /**
   * Handle Angular OnChanges lifecycle: initialize elements when the elements input becomes empty and clear cached map embed URL data when elements change.
   * @example
   * gOnChanges({ elements: { currentValue: [], previousValue: null, firstChange: true } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Object containing the changed input properties and their current/previous values.
   * @returns {{void}} Void (no return value).
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elements'] && this.elements.length === 0) {
      this.initializeElements();
    }

    // Clear map embed URL cache when elements change to recalculate
    if (changes['elements'] || changes['pageData']) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      setTimeout(() => this.updateTestimonialsScrollState());
    }

    // Note: Footer links are computed directly in template via getFooterQuickLinks()
    // which will automatically update when pages array changes due to Angular change detection
    // Header navigation comes from elements[0].content.navigation which is updated by
    // populateHeaderFromTemplate() in the parent component
  }

  private initializeElements(): void {
    this.elements = Object.keys(this.ELEMENT_CONFIG).map((key, index) => ({
      id: key,
      type: key as WebsiteElement['type'],
      content: {},
      selected: false,
    }));
  }

  onElementClick(element: WebsiteElement | undefined): void {
    if (!element) return;

    this.updateSelection(element);
    this.elementSelect.emit(element);
  }

  private updateSelection(selectedElement: WebsiteElement): void {
    this.elements.forEach(e => (e.selected = e.id === selectedElement.id));
  }

  getElementByIndex(index: number): WebsiteElement | undefined {
    return this.elements[index];
  }

  getWelcomeElement(): WebsiteElement | undefined {
    return this.elements?.find(
      e => e.id === 'welcome' || (e as any).originalKey === 'info_section'
    );
  }

  getServicesElement(): WebsiteElement | undefined {
    return this.elements?.find(
      e =>
        e.id === 'services' ||
        e.id === 'welcome_info_section_1' ||
        (e as any).originalKey === 'info_section_1'
    );
  }

  getBookingElement(): WebsiteElement | undefined {
    return this.elements?.find(
      e =>
        e.id === 'booking' || e.type === 'booking' || (e as any).originalKey === 'booking_section'
    );
  }

  /**
   * Safely get title as string (prevents [object Object] display)
   */
  getTitleSafe(element: WebsiteElement | undefined): string {
    if (!element?.content?.title) return '';
    const title = element.content.title;
    if (typeof title === 'string') return title;
    if (typeof title === 'object' && title !== null) {
      // If it's an object, try to get a text property or convert to string
      return (title as any).label || (title as any).text || (title as any).value || '';
    }
    return String(title);
  }

  /**
   * Safely get description as string (prevents [object Object] display)
   */
  getDescriptionSafe(element: WebsiteElement | undefined): string {
    if (!element?.content?.description) return '';
    const description = element.content.description;
    if (typeof description === 'string') return description;
    if (typeof description === 'object' && description !== null) {
      return (description as any).text || (description as any).value || String(description);
    }
    return String(description);
  }

  /**
   * Get title color from titleStyle (handles both string and object types)
   */
  getTitleColor(element: WebsiteElement | undefined): string | null {
    if (!element?.content?.titleStyle) {
      return null;
    }
    const titleStyle = element.content.titleStyle;
    // If it's a string, return it (it's a color)
    if (typeof titleStyle === 'string') {
      return titleStyle;
    }
    // If it's an object (from minimal theme), return null (objects don't contain color info)
    return null;
  }

  /**
   * Check if an element should be visible based on section visibility settings
   */
  isElementVisible(elementId: string): boolean {
    const normalizedId =
      elementId === 'info' ? 'welcome' : elementId === 'service_highlight' ? 'services' : elementId;

    const elementExists = this.elements?.some(el =>
      this.elementMatchesVisibilityId(el, normalizedId)
    );
    if (!elementExists) {
      return false;
    }

    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[normalizedId] !== false;
  }

  private elementMatchesVisibilityId(el: WebsiteElement, elementId: string): boolean {
    if (el.id === elementId) return true;

    const originalKey = (el as any).originalKey;
    if (originalKey === elementId) return true;

    if (elementId === 'welcome' && (el.id === 'welcome' || originalKey === 'info_section')) {
      return true;
    }

    if (
      elementId === 'services' &&
      (el.id === 'services' ||
        el.id === 'welcome_info_section_1' ||
        originalKey === 'info_section_1')
    ) {
      return true;
    }

    return false;
  }

  getElementClass(type: string): string {
    return this.ELEMENT_CONFIG[type as keyof typeof this.ELEMENT_CONFIG]?.class || '';
  }

  getFormRows(formFields: any[]): any[][] {
    if (!formFields || formFields.length === 0) return [];

    const rows: any[][] = [];
    for (let i = 0; i < formFields.length; i += 2) {
      const row = formFields.slice(i, i + 2);
      rows.push(row);
    }
    return rows;
  }

  // Header data helpers for shared component
  /**
   * Retrieve the header logo configuration from page data or fallback header element.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/', alt: 'Logo' }
   * @returns {{Logo|null}} The resolved header logo object or null if none is found.
   */
  getHeaderLogo(): Logo | null {
    // Priority 1: Check json_data.website.header.logo (header section logo)
    if (this.pageData?.json_data?.website?.header?.logo) {
      const headerLogo = this.pageData.json_data.website.header.logo;
      if (headerLogo.url || headerLogo.image) {
        return {
          image: headerLogo.url || headerLogo.image || '',
          text: headerLogo.text || 'Logo',
          link: headerLogo.link || '/',
          alt: headerLogo.alt || 'Logo',
        };
      }
      if (headerLogo.text) {
        return {
          image: '',
          text: headerLogo.text,
          link: headerLogo.link || '/',
          alt: headerLogo.alt || 'Logo',
        };
      }
    }

    // Priority 2: Check header element content (fallback)
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.logo) {
      const logoContent = headerElement.content.logo as any;
      return {
        image: logoContent.image || logoContent.url || '',
        alt: logoContent.alt || 'Logo',
        text: logoContent.text,
        link: logoContent.link || '/',
      };
    }
    return null;
  }

  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.navigation) {
      return headerElement.content.navigation.map((nav: any) => ({
        label: nav.label,
        link: nav.link,
        active: nav.active,
      }));
    }
    return [];
  }

  /**
   * Get the call-to-action (CTA) button from the first header element, returning a default empty button if none exists.
   * @example
   * etHeaderCTAButton()
   * { text: 'Learn more' }
   * @returns {CTAButton | null} CTA button object containing a text property, or null if no header is present.
   */
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getElementByIndex(0);
    const headerContent = headerElement?.content as any;
    if (headerContent?.ctaButton) {
      return buildHeaderCtaButton(headerContent.ctaButton);
    }
    return buildHeaderCtaButton(null);
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getElementByIndex(0);
    if (headerElement?.content?.backgroundColor) {
      return headerElement.content.backgroundColor as string;
    }
    return null;
  }

  isHeaderSelected(): boolean {
    return this.getElementByIndex(0)?.selected || false;
  }

  onHeaderClick(): void {
    const headerElement = this.getElementByIndex(0);
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer' || e.type === 'footer');
  }

  /**
   * Determine and return the hero layout identifier ('layout_1' through 'layout_7') by inspecting the component's first hero element content; falls back to 'layout_1'.
   * @example
   * etHeroLayout()
   * 'layout_3'
   * @param {void} none - No arguments.
   * @returns {'layout_1'|'layout_2'|'layout_3'|'layout_4'|'layout_5'|'layout_6'|'layout_7'} Return the resolved layout identifier string.
   **/
  getHeroLayout():
    'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7' {
    const heroElement = this.getElementByIndex(1);
    if (!heroElement) return 'layout_1';

    const layout = heroElement.content?.layout;
    if (!layout) return 'layout_1';

    // Handle API structure: layout is an object with type property
    if (typeof layout === 'object' && layout !== null && 'type' in layout) {
      const layoutType = (layout as any).type;
      if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
        return layoutType as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
      // If type is just a number, convert to layout_X format
      if (typeof layoutType === 'string' && /^[1-7]$/.test(layoutType)) {
        return `layout_${layoutType}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
    }

    // Handle old format: layout is a string like '1', '2', etc.
    if (typeof layout === 'string') {
      if (layout.startsWith('layout_')) {
        return layout as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
      if (/^[1-7]$/.test(layout)) {
        return `layout_${layout}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
    }

    return 'layout_1';
  }

  /**
   * Get the background color for the hero element at index 1 of the component's elements.
   * @example
   * etHeroBackgroundColor()
   * "#ffffff"
   * @returns {string} Returns the hero background color if available, otherwise an empty string.
   **/
  getHeroBackgroundColor(): string {
    return this.getSectionBackgroundColor(this.getElementByIndex(1));
  }

  /**
   * Resolve section background from live element content, then page JSON (layout.background_color).
   */
  getSectionBackgroundColor(element: WebsiteElement | null | undefined): string {
    if (!element) {
      return '';
    }

    const content = element.content as any;
    if (
      content?.backgroundColor !== undefined &&
      content?.backgroundColor !== null &&
      content.backgroundColor !== ''
    ) {
      return content.backgroundColor;
    }

    const sectionKey = this.getSectionKeyForElement(element);
    const sections =
      this.pageData?.page?.sections || this.pageData?.json_data?.page?.sections || null;
    const sectionFromJson = sectionKey && sections ? sections[sectionKey] : null;
    const jsonBg = sectionFromJson?.layout?.background_color;
    if (jsonBg !== undefined && jsonBg !== null && jsonBg !== '') {
      return jsonBg;
    }

    const layout = content?.layout;
    if (typeof layout === 'object' && layout !== null && layout.background_color) {
      return layout.background_color;
    }

    return '';
  }

  private getSectionKeyForElement(element: WebsiteElement): string | null {
    const originalKey = (element as any).originalKey;
    if (originalKey) {
      return originalKey;
    }
    if (element.id === 'welcome') {
      return 'info_section';
    }
    if (element.id?.startsWith('welcome_')) {
      return element.id.replace('welcome_', '');
    }
    if (element.id === 'services') {
      return 'info_section_1';
    }
    if (element.id === 'hero') {
      return 'hero_section';
    }
    if (element.id === 'testimonials') {
      return 'testimonials_section';
    }
    if (element.id === 'booking') {
      return 'booking_section';
    }
    if (element.id === 'contact') {
      return 'contact_section';
    }
    return null;
  }

  getWelcomeLayout(): '1' | '2' | '3' {
    const welcomeElement = this.getWelcomeElement();
    return (welcomeElement?.content?.layout as '1' | '2' | '3') || '1';
  }

  getContactElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'contact') || this.getElementByIndex(6);
  }

  getContactSubtitle(): string {
    const el = this.getContactElement();
    if (!el?.content) return '';
    return this.getDescriptionSafe(el) || el.content.subtitle || '';
  }

  getContactBusinessHours(): {
    day?: string;
    days?: string;
    time?: string;
    openTime?: string;
    closed?: boolean;
  }[] {
    const hours = this.getContactElement()?.content?.businessHours;
    return Array.isArray(hours) ? hours : [];
  }

  hasBusinessHours(): boolean {
    return this.getContactBusinessHours().length > 0;
  }

  /**
   * Return a sanitized embed URL for the map from the component's contact content or null if unavailable/invalid.
   * @example
   * etMapEmbedUrl()
   * SafeResourceUrl | null
   * @returns {SafeResourceUrl | null} Sanitized SafeResourceUrl for embedding the map, or null when no valid http(s) URL can be extracted.
   **/
  getMapEmbedUrl(): SafeResourceUrl | null {
    const contactElement = this.getContactElement();
    const mapEmbedUrl = contactElement?.content?.mapEmbedUrl;

    if (!mapEmbedUrl || typeof mapEmbedUrl !== 'string') {
      // Clear cache if no URL
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Extract URL from iframe HTML if needed
    const trimmedUrl = mapEmbedUrl.trim();

    // Check if we have a cached value for this URL
    if (this.cachedMapEmbedUrl && this.cachedMapEmbedUrlSource === trimmedUrl) {
      return this.cachedMapEmbedUrl;
    }

    // Process the URL
    let processedUrl: string | null = null;

    // If it's already a clean URL (starts with http:// or https://), use it directly
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      processedUrl = trimmedUrl;
    }
    // If it contains iframe HTML, extract the src URL
    else if (trimmedUrl.includes('<iframe') || trimmedUrl.includes('&lt;iframe')) {
      try {
        // Try regex extraction first
        const srcMatch = trimmedUrl.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
        if (srcMatch) {
          const extractedUrl = (srcMatch[1] || srcMatch[2] || '').trim();
          if (extractedUrl) {
            // Decode HTML entities
            processedUrl = extractedUrl
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }
        }

        // Try DOMParser as fallback if regex didn't work
        if (!processedUrl) {
          const wrappedHtml = `<div>${trimmedUrl}</div>`;
          const parser = new DOMParser();
          const doc = parser.parseFromString(wrappedHtml, 'text/html');
          const iframe = doc.querySelector('iframe');
          if (iframe) {
            processedUrl = iframe.getAttribute('src');
          }
        }
      } catch (error) {
        this.logger.warn('Error extracting URL from iframe HTML:', error);
        this.cachedMapEmbedUrl = null;
        this.cachedMapEmbedUrlSource = null;
        return null;
      }
    }

    // If we couldn't extract a valid URL, clear cache and return null
    if (!processedUrl) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Only allow http(s) URLs — reject javascript:, data:, or any other scheme
    if (!processedUrl.startsWith('https://') && !processedUrl.startsWith('http://')) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Trust only validated http(s) URLs for iframe src binding
    this.cachedMapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(processedUrl);
    this.cachedMapEmbedUrlSource = trimmedUrl;
    return this.cachedMapEmbedUrl;
  }

  /**
   * Get footer quick links from footer element or active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Get footer social links from footer element
   */
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      // Map to ensure all required properties are present
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  /**
   * Get footer copyright text from footer element
   */
  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get footer logo from footer element content
   */
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }

    return { text: 'way' };
  }

  /**
   * Get footer background color from footer element
   */
  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  /**
   * Get testimonials array from testimonials element
   */
  ngAfterViewInit(): void {
    this.updateTestimonialsScrollState();
  }

  getTestimonials(): any[] {
    const testimonialsElement = this.getElementByIndex(3);
    const testimonials = testimonialsElement?.content?.testimonials;
    if (Array.isArray(testimonials)) {
      return testimonials;
    }
    return [];
  }

  getTestimonialsBackgroundColor(): string {
    return this.getSectionBackgroundColor(this.getElementByIndex(3));
  }

  getTestimonialsTitle(): string {
    const title = this.getTitleSafe(this.getElementByIndex(3));
    return title || "Don't take our word for it.\nHear from our customers";
  }

  getTestimonialsSubtitle(): string {
    const description = this.getDescriptionSafe(this.getElementByIndex(3));
    return (
      description ||
      "Our customers love the quality, speed, and care we put into every wash. Here's what they have to say about their experience with us."
    );
  }

  trackByTestimonialId(index: number, testimonial: any): string {
    return testimonial?.id || testimonial?.author?.name || String(index);
  }

  onTestimonialsScroll(): void {
    this.updateTestimonialsScrollState();
  }

  private updateTestimonialsScrollState(): void {
    const el = this.testimonialsScrollRef?.nativeElement;
    if (!el) {
      this.testimonialsScrollAtStart = true;
      this.testimonialsScrollAtEnd = true;
      return;
    }

    const threshold = 4;
    this.testimonialsScrollAtStart = el.scrollLeft <= threshold;
    this.testimonialsScrollAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold;
  }

  scrollTestimonials(direction: 'prev' | 'next'): void {
    const el = this.testimonialsScrollRef?.nativeElement;
    if (!el) return;

    const card = el.querySelector('.testimonial-card') as HTMLElement | null;
    const gap = 24;
    const scrollAmount = card ? card.offsetWidth + gap : 300;
    el.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  canScrollTestimonialsPrev(): boolean {
    return !this.testimonialsScrollAtStart;
  }

  canScrollTestimonialsNext(): boolean {
    return !this.testimonialsScrollAtEnd;
  }

  /**
   * Get social links from contact element
   */
  getSocialLinks(): { platform: string; link: string }[] {
    const contactElement = this.getContactElement();
    const socialLinks = contactElement?.content?.socialLinks;
    if (Array.isArray(socialLinks)) {
      return socialLinks.map((social: any) => ({
        platform: social.platform || '',
        link: social.link || social.url || '#',
      }));
    }
    return [];
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Convert a button link to a page type for navigation
   * Handles links like "/express-washes", "express-washes", "/", etc.
   */
  convertLinkToPageType(link: string | undefined): string {
    if (!link) return 'home';

    // Remove leading/trailing slashes and normalize
    const normalizedLink = link.replace(/^\/+|\/+$/g, '').toLowerCase();

    if (!normalizedLink || normalizedLink === 'home' || normalizedLink === '') return 'home';
    if (normalizedLink.includes('express-wash') || normalizedLink === 'express-washes')
      return 'express-wash-packages';
    if (
      normalizedLink.includes('express-detail') ||
      normalizedLink.includes('express-auto') ||
      normalizedLink === 'express-details'
    )
      return 'express-auto-details';
    if (normalizedLink.includes('fleet')) return 'fleet-management';
    if (normalizedLink.includes('coupon')) return 'coupons';

    // Try to match with page names
    const matchingPage = this.pages.find(p => {
      const pageName = (p.name || '').toLowerCase();
      const pagePath = (p.path || '').replace(/^\/+|\/+$/g, '').toLowerCase();
      return pageName === normalizedLink || pagePath === normalizedLink;
    });

    if (matchingPage) {
      const normalizedName = matchingPage.name.toLowerCase().trim();
      if (normalizedName === 'home') return 'home';
      if (normalizedName.includes('express wash') || normalizedName.includes('express_wash'))
        return 'express-wash-packages';
      if (
        normalizedName.includes('express detail') ||
        normalizedName.includes('express_detail') ||
        normalizedName.includes('express auto')
      )
        return 'express-auto-details';
      if (normalizedName.includes('fleet')) return 'fleet-management';
      if (normalizedName.includes('coupon')) return 'coupons';
    }

    return 'home';
  }

  /**
   * Handle button click - navigate to the page specified by buttonLink
   */
  onButtonClick(buttonLink: string | undefined, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    const pageType = this.convertLinkToPageType(buttonLink);
    this.navigationClick.emit(pageType);
  }

  /**
   * Handle booking button click - navigate to the page specified by buttonLink
   */
  onBookingButtonClick(element: WebsiteElement | undefined, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    const buttonLink = (element?.content as any)?.buttonLink;
    const pageType = this.convertLinkToPageType(buttonLink);
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#1E3A8A';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#64748B';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get title styles from element
   */
  getTitleStyles(element: WebsiteElement | null): any {
    if (!element) return {};
    const titleStyle = element.content?.titleStyle;
    if (!titleStyle) return {};

    // If titleStyle is a string (color), return empty object (handled by color binding)
    if (typeof titleStyle === 'string') {
      return {};
    }

    // If titleStyle is an object, return it
    if (typeof titleStyle === 'object') {
      return titleStyle;
    }

    return {};
  }

  /**
   * Get hero data for layout component
   */
  getHeroLayoutData() {
    const heroElement = this.getElementByIndex(1);
    if (!heroElement) return null;

    // Get background color from layout object if available
    const backgroundColor = this.getHeroBackgroundColor();

    return {
      title: heroElement.content?.title,
      subtitle: heroElement.content?.subtitle,
      description: heroElement.content?.description,
      buttonText: heroElement.content?.buttonText,
      buttonLink: heroElement.content?.buttonLink,
      image: heroElement.content?.image,
      imageUrl: heroElement.content?.imageUrl,
      backgroundColor: backgroundColor,
      titleStyle: heroElement.content?.titleStyle,
    };
  }

  // Booking form data storage
  private bookingFormData = new Map<string, Record<string, any>>();

  /**
   * Get booking form data for a specific element
   */
  getBookingFormData(element: WebsiteElement): Record<string, any> {
    const elementId = element?.id || 'booking';
    return this.bookingFormData.get(elementId) || {};
  }

  /**
   * Get booking layout type
   */
  getBookingLayout(element: WebsiteElement): 'layout_1' | 'layout_2' {
    if (!element) return 'layout_1';

    const layout = element.content?.layout;
    if (!layout) return 'layout_1';

    // Handle API structure: layout is an object with type property
    if (typeof layout === 'object' && layout !== null && 'type' in layout) {
      const layoutType = (layout as any).type;
      if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
        return layoutType === 'layout_2' ? 'layout_2' : 'layout_1';
      }
      if (typeof layoutType === 'string' && layoutType === '2') {
        return 'layout_2';
      }
    }

    // Handle old format: layout is a string like '1', '2', etc.
    if (typeof layout === 'string') {
      if (layout === 'layout_2' || layout === '2') {
        return 'layout_2';
      }
    }

    return 'layout_1';
  }

  /**
   * Get booking layout data for layout component
   */
  getBookingLayoutData(element: WebsiteElement): BookingLayout1Data | BookingLayout2Data {
    // Extract title from title.label if it's an object, otherwise use title directly
    const title =
      typeof element.content?.title === 'object' && element.content?.title !== null
        ? (element.content.title as any).label || element.content.title
        : element.content?.title;

    // Extract button text and colors from button object
    // Check both structures: content.button.backgroundColor (editor format) and content.buttonBackgroundColor (website preview format)
    const content = element.content as any;
    const buttonText =
      content?.button?.text || content?.buttonText || content?.button?.label || 'Book Appointment';
    const buttonBackgroundColor =
      content?.button?.backgroundColor ||
      content?.button?.style?.background ||
      content?.buttonBackgroundColor ||
      null;
    const buttonTextColor =
      content?.button?.textColor ||
      content?.button?.style?.color ||
      content?.buttonTextColor ||
      null;

    return {
      title: title || '',
      description: element.content?.description || element.content?.subtitle || '',
      buttonText: buttonText,
      buttonBackgroundColor: buttonBackgroundColor,
      buttonTextColor: buttonTextColor,
      backgroundColor: element.content?.backgroundColor,
    };
  }

  /**
   * Handle booking form submit from layout component
   */
  onBookingLayoutSubmit(
    element: WebsiteElement,
    formData: Record<string, any>,
    event: Event
  ): void {
    event.preventDefault();
    event.stopPropagation();
    // Handle form submission here
    this.logger.debug('Booking form submitted:', formData);
  }

  /**
   * Handle field change from layout component
   */
  onBookingLayoutFieldChange(element: WebsiteElement, field: any, value: any): void {
    const elementId = element?.id || 'booking';
    let formData = this.bookingFormData.get(elementId);
    if (!formData) {
      formData = {};
      this.bookingFormData.set(elementId, formData);
    }
    const fieldKey = field?.name || field?.id;
    if (fieldKey) {
      formData[fieldKey] = value;
    }
  }

  /**
   * Handle date/time change from layout component
   */
  onBookingLayoutDateTimeChange(element: WebsiteElement, field: any, dateTime: Date | null): void {
    const elementId = element?.id || 'booking';
    let formData = this.bookingFormData.get(elementId);
    if (!formData) {
      formData = {};
      this.bookingFormData.set(elementId, formData);
    }
    const dateKey = field?.name || field?.id;
    if (dateTime && dateKey) {
      const dateStr = dateTime.toISOString().split('T')[0];
      formData[dateKey] = dateStr;
      // Also set time if available
      const hours = dateTime.getHours().toString().padStart(2, '0');
      const minutes = dateTime.getMinutes().toString().padStart(2, '0');
      formData[`${dateKey}_time`] = `${hours}:${minutes}`;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
